import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useConfig, useServerData } from '@xingternal/hops';
import { useFrameHooks } from '@xingternal/hub';
import { mediaSmallOrTiny } from '@xingternal/layout-tokens';

import { LOSP_MARKETING_CAMPAIGN_TEST } from '../../experiments';
import useScrollToHash from '../../hooks/useScrollToHash';
import useSpecificExperimentTrackingData from '../../hooks/useSpecificExperimentTrackingData';
import CustomFrame from './../../Layouts/CustomFrame';
import HtmlHead from './../../Components/HtmlHead/HtmlHead';
import GdprImage from './../../Components/Illustrations/GdprImage';
import { PROP_CHANNEL } from '../../Components/PageviewTracker/PageviewTracker';
import ttt from '../../Components/PageviewTracker/initializedTtt';
import useCookieConsent from './../../contexts/useCookieConsent';
import ContentSection from './ContentSection/ContentSection';
import HeaderSection from './HeaderSection/HeaderSection';
import DownloadAppSection from './DownloadAppSection/DownloadAppSection';
import CtaSection from './CtaSection/CtaSection';
import JobsfinderSection from './JobsfinderSection/JobsfinderSection';
import KeyFeaturesSection from './KeyFeaturesSection/KeyFeaturesSection';

import cultureCheckIllustrationDe from './images/culturecheck_de.svg';
import cultureCheckIllustrationEn from './images/culturcheck_en.svg';
import cultureCompassIllustrationDe from './images/culturecompass_de.svg';
import cultureCompassIllustrationEn from './images/culturecompass_en.svg';
import jobsIllustration from './images/jobs.svg';
import lebenslaufIllustration from './images/lebenslauf.svg';
import networkIllustration from './images/network.svg';

import { GdprWrapper } from './Start.style';

const CULTURE_CHECK_SECTION_ID = 'kulturcheck';

const Start = () => {
  useScrollToHash();
  const { externalUrl, externalLoginUrl } = useConfig();
  const { language } = useServerData();
  const { search } = useLocation();
  const loginUrl = new URL(search, externalLoginUrl).toString();
  const signUpUrl = `${externalUrl}/start/signup`;
  const signUpActionName = 'losp_sign_up_button_click';
  const headerSignUpTrackingParams = `sc_o=${signUpActionName}&sc_o_PropActionOrigin=${signUpActionName}_header`;
  const cultureCheckTrackingParams = `sc_o=${signUpActionName}&sc_o_PropActionOrigin=${signUpActionName}_cultural_assessment`;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const { matches } = window.matchMedia(mediaSmallOrTiny);
      let context = {};

      if (matches) {
        context = experimentTrackingData;
      }

      ttt.pageview(PROP_CHANNEL, `${PROP_CHANNEL}/start/index`, undefined, {
        PropContextDimension1: 'welcome_lo_startpage_signup_view',
        ...context,
      });
    }
  }, []);

  const { useExperiment } = useFrameHooks();
  const experimentTrackingData = useSpecificExperimentTrackingData(
    LOSP_MARKETING_CAMPAIGN_TEST.trackingId
  );
  const LOSP_VARIANT_TEST_VARIANT = useExperiment(
    LOSP_MARKETING_CAMPAIGN_TEST.name
  );
  const isJobsSearchWhiteVersion = LOSP_VARIANT_TEST_VARIANT === 'A';
  const isJobsSearchWhiteLessFiltersVersion = LOSP_VARIANT_TEST_VARIANT === 'B';
  const isJobsSearchWhiteNoImageVersion = LOSP_VARIANT_TEST_VARIANT === 'C';

  // Redirect to https://www.xing.com/via/career-guidance/company-culture-preferences/assessment
  const cultureCheckRedirectToParam =
    'register_redirect_to=6e1b0f2c28822051.aHR0cHM6Ly93d3cueGluZy5jb20vdmlhL2NhcmVlci1ndWlkYW5jZS9jb21wYW55LWN1bHR1cmUtcHJlZmVyZW5jZXMvYXNzZXNzbWVudA';
  const cultureCheckDestUrlParam = `dest_url=${encodeURIComponent(
    'https://www.xing.com/via/career-guidance/company-culture-preferences/assessment'
  )}`;

  const cultureCheckIllustration =
    language === 'de' ? cultureCheckIllustrationDe : cultureCheckIllustrationEn;
  const cultureCompassIllustration =
    language === 'de'
      ? cultureCompassIllustrationDe
      : cultureCompassIllustrationEn;

  useCookieConsent();

  return (
    <React.Fragment>
      <HtmlHead pageName={'startPage'}>
        <link rel={'canonical'} href={externalUrl} />
      </HtmlHead>
      <CustomFrame
        signupLink={`${signUpUrl}?${headerSignUpTrackingParams}`}
        loginLink={loginUrl}
      >
        <main>
          <HeaderSection
            isJobsSearchWhiteVersion={isJobsSearchWhiteVersion}
            isJobsSearchWhiteLessFiltersVersion={
              isJobsSearchWhiteLessFiltersVersion
            }
            isJobsSearchWhiteNoImageVersion={isJobsSearchWhiteNoImageVersion}
          />
          {!isJobsSearchWhiteVersion &&
          !isJobsSearchWhiteLessFiltersVersion &&
          !isJobsSearchWhiteNoImageVersion ? (
            <>
              <ContentSection
                scrollToId={CULTURE_CHECK_SECTION_ID}
                imageOnTheRight={true}
                headlineTranslationId="WELCOME_STARTPAGE_CULTURE_CHECK_TITLE"
                textTranslationId="WELCOME_STARTPAGE_CULTURE_CHECK_TEXT"
                imageSrc={cultureCheckIllustration}
                imageAltTranslationId="WELCOME_STARTPAGE_CULTURE_CHECK_IMG_ALT"
                buttonTextTranslationId="WELCOME_STARTPAGE_CULTURE_CHECK_BUTTON"
                link={`${signUpUrl}?${cultureCheckDestUrlParam}&${cultureCheckRedirectToParam}&${cultureCheckTrackingParams}`}
              />
              <ContentSection
                headlineTranslationId="WELCOME_STARTPAGE_CULTURE_COMPASS_TITLE"
                imageOnTheRight={false}
                textTranslationId="WELCOME_STARTPAGE_CULTURE_COMPASS_TEXT"
                imageSrc={cultureCompassIllustration}
                imageAltTranslationId="WELCOME_STARTPAGE_CULTURE_COMPASS_IMG_ALT"
                buttonTextTranslationId="WELCOME_STARTPAGE_CULTURE_COMPASS_BUTTON"
                link="https://www.xing.com/news/articles/finde-nicht-nur-den-richtigen-job-sondern-auch-das-beste-unternehmen-mit-dem-kulturkompass-4539801?xing_share=news_"
              />
              <JobsfinderSection />
              <KeyFeaturesSection />
            </>
          ) : (
            <>
              <ContentSection
                headlineTranslationId="WELCOME_STARTPAGE_JOBS_VERSION_SECTION_ONE_TITLE"
                headlineTranslationDefaultMessage="On XING, you're more than just your CV"
                textTranslationId="WELCOME_STARTPAGE_JOBS_VERSION_SECTION_ONE_TEXT"
                textTranslationDefaultMessage="Whether it's a change of city, lifestyle or just wanting something new – on XING it’s all about who you want to be in your working life. Let the real you shine through."
                imageSrc={lebenslaufIllustration}
                imageAltTranslationId="WELCOME_STARTPAGE_JOBS_VERSION_SECTION_ONE_IMG_ALT"
              />
              <ContentSection
                imageOnTheRight={true}
                headlineTranslationId="WELCOME_STARTPAGE_JOBS_VERSION_SECTION_TWO_TITLE"
                headlineTranslationDefaultMessage="1.5 mill jobs and countless opportunities"
                textTranslationId="WELCOME_STARTPAGE_JOBS_VERSION_SECTION_TWO_TEXT"
                textTranslationDefaultMessage="Imagine a gallery of amazing jobs where you receive suggestions tailored to your preferences. Find out what you really want from your working life: how do you want to develop? Which employers do you like? What company culture do you want to be part of?"
                imageSrc={jobsIllustration}
                imageAltTranslationId="WELCOME_STARTPAGE_JOBS_VERSION_SECTION_TWO_IMG_ALT"
              />
              <ContentSection
                headlineTranslationId="WELCOME_STARTPAGE_JOBS_VERSION_SECTION_THREE_TITLE"
                headlineTranslationDefaultMessage="Reach out to the right people"
                textTranslationId="WELCOME_STARTPAGE_JOBS_VERSION_SECTION_THREE_TEXT"
                textTranslationDefaultMessage="Over 23,000 recruiters on XING are just a message away. Get in touch with HR, industry and labour market experts, and personally contact recruiters and employees at your dream company. Join us and do your XING!"
                imageSrc={networkIllustration}
                imageAltTranslationId="WELCOME_STARTPAGE_JOBS_VERSION_SECTION_THREE_IMG_ALT"
              />
            </>
          )}
          <CtaSection />
          <DownloadAppSection />
        </main>
        <GdprWrapper>
          <GdprImage />
        </GdprWrapper>
      </CustomFrame>
    </React.Fragment>
  );
};

export default Start;
